/**
 * WebSocket Context - Real-time Notifications
 * 
 * Provides global WebSocket connection management and real-time
 * toast notifications for all connected users.
 * 
 * Features:
 * - Auto-connect on authentication
 * - Auto-reconnect with exponential backoff
 * - World-class toast notifications (Linear/Notion style)
 * - Event-specific refresh triggers
 */
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';
import { authService } from '../services/authService';
import { PackagePlus, PackageCheck, PackageX, RotateCcw, Bell, Wifi, WifiOff } from 'lucide-react';

// WebSocket event types we care about
const PART_EVENTS = ['part.created', 'part.updated', 'part.deleted', 'part.restored'];

const WebSocketContext = createContext(null);

/**
 * Custom toast notification component - Premium SaaS style
 */
const PartNotification = ({ event, onDismiss }) => {
  const getIcon = () => {
    switch (event.event_type) {
      case 'part.created':
        return <PackagePlus className="w-5 h-5 text-emerald-500" />;
      case 'part.updated':
        return <PackageCheck className="w-5 h-5 text-blue-500" />;
      case 'part.deleted':
        return <PackageX className="w-5 h-5 text-amber-500" />;
      case 'part.restored':
        return <RotateCcw className="w-5 h-5 text-violet-500" />;
      default:
        return <Bell className="w-5 h-5 text-slate-500" />;
    }
  };

  const getAccentColor = () => {
    switch (event.event_type) {
      case 'part.created':
        return 'border-l-emerald-500 bg-gradient-to-r from-emerald-50/80 to-white';
      case 'part.updated':
        return 'border-l-blue-500 bg-gradient-to-r from-blue-50/80 to-white';
      case 'part.deleted':
        return 'border-l-amber-500 bg-gradient-to-r from-amber-50/80 to-white';
      case 'part.restored':
        return 'border-l-violet-500 bg-gradient-to-r from-violet-50/80 to-white';
      default:
        return 'border-l-slate-400 bg-white';
    }
  };

  const getLabel = () => {
    switch (event.event_type) {
      case 'part.created':
        return 'New Part';
      case 'part.updated':
        return 'Updated';
      case 'part.deleted':
        return 'Archived';
      case 'part.restored':
        return 'Restored';
      default:
        return 'Update';
    }
  };

  return (
    <div 
      className={`
        flex items-start gap-3 p-4 rounded-lg border-l-4 shadow-lg 
        backdrop-blur-sm min-w-[320px] max-w-[400px]
        ${getAccentColor()}
        animate-in slide-in-from-right-5 duration-300
      `}
      data-testid="part-notification"
    >
      <div className="flex-shrink-0 mt-0.5">
        {getIcon()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            {getLabel()}
          </span>
          {event.data?.actor_name && (
            <>
              <span className="text-slate-300">•</span>
              <span className="text-xs text-slate-400 truncate">
                by {event.data.actor_name}
              </span>
            </>
          )}
        </div>
        <p className="text-sm font-medium text-slate-900 truncate font-mono">
          {event.data?.hpn_display || event.data?.hpn_compact || 'Part'}
        </p>
        {event.data?.description && (
          <p className="text-xs text-slate-500 truncate mt-0.5">
            {event.data.description}
          </p>
        )}
      </div>
    </div>
  );
};

export const WebSocketProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  // Get token directly from authService since AuthContext doesn't expose it
  const token = authService.getToken();
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const [isConnected, setIsConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);
  
  // Event listeners for specific event types
  const eventListenersRef = useRef({});
  
  // Subscribe to specific event types
  const subscribe = useCallback((eventType, callback) => {
    if (!eventListenersRef.current[eventType]) {
      eventListenersRef.current[eventType] = new Set();
    }
    eventListenersRef.current[eventType].add(callback);
    
    // Return unsubscribe function
    return () => {
      eventListenersRef.current[eventType]?.delete(callback);
    };
  }, []);
  
  // Notify all listeners for an event type
  const notifyListeners = useCallback((eventType, event) => {
    const listeners = eventListenersRef.current[eventType];
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(event);
        } catch (e) {
          console.error('Error in WebSocket event listener:', e);
        }
      });
    }
  }, []);
  
  // Show world-class toast notification
  const showPartNotification = useCallback((event) => {
    toast.custom(
      (t) => <PartNotification event={event} onDismiss={() => toast.dismiss(t)} />,
      {
        duration: 5000,
        position: 'bottom-right',
      }
    );
  }, []);
  
  // Handle incoming WebSocket messages
  const handleMessage = useCallback((event) => {
    try {
      const data = JSON.parse(event.data);
      const eventType = data.event_type;
      
      // Skip heartbeat and connection ack
      if (eventType === 'heartbeat' || eventType === 'connection.ack') {
        return;
      }
      
      setLastEvent(data);
      
      // Show toast for part events
      if (PART_EVENTS.includes(eventType)) {
        showPartNotification(data);
      }
      
      // Notify specific listeners
      notifyListeners(eventType, data);
      
      // Also notify wildcard listeners
      notifyListeners('*', data);
      
    } catch (e) {
      console.error('Error parsing WebSocket message:', e);
    }
  }, [showPartNotification, notifyListeners]);
  
  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!isAuthenticated || !token) return;
    
    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    // Build WebSocket URL
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    
    // Extract host from backend URL
    let wsHost;
    try {
      const url = new URL(backendUrl);
      wsHost = url.host;
    } catch {
      wsHost = window.location.host;
    }
    
    const wsUrl = `${wsProtocol}//${wsHost}/api/v1/inventory/v2/ws/dashboard?token=${token}`;
    
    try {
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        console.log('[WebSocket] Connected');
        setIsConnected(true);
        reconnectAttempts.current = 0;
      };
      
      ws.onmessage = handleMessage;
      
      ws.onerror = (error) => {
        console.error('[WebSocket] Error:', error);
      };
      
      ws.onclose = (event) => {
        console.log('[WebSocket] Disconnected:', event.code, event.reason);
        setIsConnected(false);
        
        // Attempt reconnection with exponential backoff
        if (isAuthenticated && event.code !== 4001) { // 4001 = auth required
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          reconnectAttempts.current++;
          
          console.log(`[WebSocket] Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current})`);
          reconnectTimeoutRef.current = setTimeout(connect, delay);
        }
      };
      
      wsRef.current = ws;
    } catch (e) {
      console.error('[WebSocket] Connection error:', e);
    }
  }, [isAuthenticated, token, handleMessage]);
  
  // Connect when authenticated
  useEffect(() => {
    if (isAuthenticated && token) {
      connect();
    }
    
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [isAuthenticated, token, connect]);
  
  // Send message through WebSocket
  const sendMessage = useCallback((type, payload = {}) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type, ...payload }));
    }
  }, []);
  
  const value = {
    isConnected,
    lastEvent,
    subscribe,
    sendMessage,
  };
  
  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

/**
 * Hook to access WebSocket context
 */
export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

/**
 * Hook to subscribe to specific WebSocket events
 * 
 * @param {string} eventType - Event type to listen for (e.g., 'part.created')
 * @param {function} callback - Callback function when event received
 */
export const useWebSocketEvent = (eventType, callback) => {
  const { subscribe } = useWebSocket();
  
  useEffect(() => {
    const unsubscribe = subscribe(eventType, callback);
    return unsubscribe;
  }, [eventType, callback, subscribe]);
};

/**
 * Hook specifically for part events with auto-refresh capability
 */
export const usePartEvents = (onPartEvent) => {
  const { subscribe } = useWebSocket();
  
  useEffect(() => {
    const unsubscribers = PART_EVENTS.map(eventType => 
      subscribe(eventType, onPartEvent)
    );
    
    return () => {
      unsubscribers.forEach(unsub => unsub());
    };
  }, [subscribe, onPartEvent]);
};

export default WebSocketContext;
